import { useEffect, useRef, useState } from 'react';
import type { ComponentType, SVGProps } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogoMark } from '../brand/LogoMark';
import { Avatar } from '../ui/Avatar';
import { Modal } from '../ui/Modal';
import { api } from '../../lib/api';
import { clearTokens, getRefreshToken, isReadOnly } from '../../lib/tokens';
import { featureFlags } from '../../lib/featureFlags';
import { LayersIcon } from '../icons/MapToolIcons';

type NavIcon = {
  label: string;
  icon?: string;
  IconComponent?: ComponentType<SVGProps<SVGSVGElement>>;
  to?: string;
  onClick?: () => void;
};

export function PrimaryNavRail({
  userInitials,
  userAvatarUrl,
}: {
  userInitials: string;
  userAvatarUrl?: string | null;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleLogout = async () => {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      // Best-effort — the local tokens get cleared either way, this just also
      // revokes the session server-side so it shows up as a real "выход" in
      // the audit log instead of just quietly expiring in 30 days.
      await api.logout(refreshToken).catch(() => {});
    }
    clearTokens();
    navigate('/login');
  };

  const readOnly = isReadOnly();

  const items: NavIcon[] = [
    { label: 'Карта', icon: '⌂', to: '/app' },
    { label: 'Справочники', icon: '▦', to: '/app/dictionaries' },
    { label: 'Слои', IconComponent: LayersIcon, to: '/app/data' },
    { label: 'Маршруты', icon: '⌁' },
    // Прототип под тендер по наружному освещению — см. src/lib/featureFlags.ts
    // (reportsPrototype). Без флага остаётся тем же disabled-плейсхолдером «скоро»,
    // каким был раньше.
    { label: 'Аналитика', icon: '◫', ...(featureFlags.reportsPrototype ? { to: '/app/reports' } : {}) },
    ...(readOnly ? [] : [{ label: 'Настройки карты', icon: '⚙', to: '/app/map-settings' }]),
  ];

  return (
    <aside className="flex h-screen w-[84px] shrink-0 flex-col items-center bg-bg-navigation py-6">
      <LogoMark size={30} />

      <nav className="mt-10 flex flex-col gap-3">
        {items.map((item) => {
          const isActive = item.to != null && location.pathname === item.to;
          const content = item.IconComponent ? (
            <item.IconComponent className="size-5" />
          ) : (
            <span className="text-[19px] leading-none">{item.icon}</span>
          );
          const className = `flex size-14 items-center justify-center rounded-xl transition-colors ${
            isActive
              ? 'bg-action-primary text-text-inverse'
              : 'text-text-secondary hover:bg-bg-surface/10 hover:text-text-inverse'
          }`;

          if (item.to) {
            return (
              <Link key={item.label} to={item.to} title={item.label} className={className}>
                {content}
              </Link>
            );
          }
          if (item.onClick) {
            return (
              <button key={item.label} type="button" title={item.label} onClick={item.onClick} className={className}>
                {content}
              </button>
            );
          }
          return (
            <span
              key={item.label}
              title={`${item.label} — скоро`}
              className={`${className} opacity-40`}
            >
              {content}
            </span>
          );
        })}
      </nav>

      <div ref={menuRef} className="relative z-50 mt-auto">
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          title="Профиль"
          className="flex size-[42px] items-center justify-center overflow-hidden rounded-full bg-ai-accent text-xs font-semibold text-text-inverse"
        >
          <Avatar initials={userInitials} imageUrl={userAvatarUrl} size={42} />
        </button>

        {menuOpen && (
          <div className="absolute bottom-0 left-full z-50 ml-2 w-44 rounded-xl border border-border-default bg-bg-surface p-1.5 shadow-lg">
            <Link
              to="/app/profile"
              onClick={() => setMenuOpen(false)}
              className="flex h-10 items-center rounded-lg px-3 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
            >
              Профиль
            </Link>
            <Link
              to="/app/organization"
              onClick={() => setMenuOpen(false)}
              className="flex h-10 items-center rounded-lg px-3 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
            >
              Организация
            </Link>
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                setConfirmOpen(true);
              }}
              className="flex h-10 w-full items-center rounded-lg px-3 text-left text-[13px] font-medium text-status-danger hover:bg-bg-surface-secondary"
            >
              Выход
            </button>
          </div>
        )}
      </div>

      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <h2 className="text-lg font-semibold text-text-primary">Выйти из аккаунта?</h2>
        <p className="mt-2 text-sm text-text-secondary">Точно хотите выйти? Для продолжения работы нужно будет войти заново.</p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setConfirmOpen(false)}
            className="flex h-10 items-center rounded-md border border-border-strong bg-bg-surface px-4 text-sm font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            Нет
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="flex h-10 items-center rounded-md bg-status-danger px-4 text-sm font-medium text-text-inverse hover:opacity-90"
          >
            Да, выйти
          </button>
        </div>
      </Modal>
    </aside>
  );
}
